import * as React from "react"
import { useState } from "react"
import { yupResolver } from "@hookform/resolvers/yup"
import { Controller, useForm } from "react-hook-form"
import { useCreateApplyFormMutation } from "@graphql/generated"
import {
    emailRequired, functionRequired, stringRequiredMax, Validator
} from "@utils/validation"
import InputField from "@components/FormFields/InputField"
import { Container } from "@components/styles"
import Button from "@components/Button"
import RadioGroup from "@components/FormFields/RadioGroup"
import { FormMessageProps } from "@components/FormMessage/types"
import FormMessage from "@components/FormMessage"
import phoneRequired from "@utils/validation/phoneRequired"
import { ApplyFormInput, ApplyFormProps } from "./types"
import {
    ButtonWrapper, ContentWrapper, FormContainer, StyledForm, Wrapper
} from "./styles"

const getMessage = (functionTitle: string) => {
    if (functionTitle === "coach" || functionTitle === "trainer") {
        return `
            <p>Wat leuk dat jij je als coach/trainer hebt aangemeld voor Play Unified op de club!</p> 
            <p><a href="https://playunified.nl/PUK-coach-trainer/" target="_blank">KLIK HIER</a> voor de Play Unified-kit voor coaches en trainers.</p>
            <p>Hier staan diverse voorbeelden om jullie zoveel mogelijk te ondersteunen in het organiseren van een Play Unified activiteit.</p>
            <p>Succes maar vooral ook veel plezier!</p>
        `
    } if (functionTitle === "sportbond") {
        return `
            <p>Wat tof dat jullie als sportbond meedoen aan Play Unified op de club!</p>
            <p><a href="https://playunified.nl/PUK-sportbond/" target="_blank">KLIK HIER</a> voor de Play Unified-kit voor sportbonden.</p>
            <p>Hier staan diverse voorbeelden om jullie zoveel mogelijk te ondersteunen bij het informeren van jullie clubs.</p>
            <p>Heel veel dank!</p>
        `
    }

    return `
            <p>Wat leuk dat jullie je als club hebben aangemeld voor Play Unified op de club!</p> 
            <p><a href="https://playunified.nl/PUK-club/" target="_blank">KLIK HIER</a> voor de Play Unified-kit voor clubs.</p> 
            <p>Hier staan diverse voorbeelden om jullie zoveel mogelijk te ondersteunen in het organiseren van een Play Unified activiteit!</p>
            <p>Succes maar vooral ook veel plezier!</p>
    `
}

const ApplyForm: React.FC<ApplyFormProps> = ({ content }) => {
    const [formMessage, setFormMessage] = useState<FormMessageProps | null>(null)
    const validationSchema = Validator({
        email: emailRequired(),
        name: stringRequiredMax(50),
        surname: stringRequiredMax(50),
        sport: stringRequiredMax(50),
        clubAndAddress: stringRequiredMax(250),
        function: functionRequired(),
        phoneNumber: phoneRequired()
    })

    const {
        register, control, reset, handleSubmit, formState: { errors }
    } = useForm({
        mode: "onBlur",
        resolver: yupResolver(validationSchema)
    })

    const [
        { fetching }, CreateApplyFormMutation
    ] = useCreateApplyFormMutation()

    const onSubmit = async (formData: ApplyFormInput) => {
        setFormMessage(null)

        CreateApplyFormMutation({ data: formData }).then((result) => {
            if (result.data?.createApplyForm?.data?.id) {
                reset()
                setFormMessage({
                    type: "success",
                    message: getMessage(formData.function)
                })
            } else {
                setFormMessage({
                    type: "error",
                    message: "Er is een fout opgetreden, probeer het opnieuw."
                })
            }
        })
    }
    return <Wrapper>
        <Container>
            {content && <ContentWrapper dangerouslySetInnerHTML={{ __html: content }} />}
            <FormContainer>
                {formMessage && <FormMessage {...formMessage} />}
                <StyledForm onSubmit={handleSubmit(onSubmit)} noValidate>
                    <InputField
                        id="name"
                        type="text"
                        label="Naam"
                        required
                        {...register("name")}
                        error={errors.name?.message}
                    />
                    <InputField
                        id="surname"
                        type="text"
                        label="Achternaam"
                        required
                        {...register("surname")}
                        error={errors.surname?.message}
                    />
                    <InputField
                        id="email"
                        type="email"
                        label="E-mailadres"
                        {...register("email")}
                        required
                        error={errors.email?.message}
                    />
                    <InputField
                        id="phoneNumber"
                        type="text"
                        label="Telefoonnummer"
                        required
                        {...register("phoneNumber")}
                        error={errors.phoneNumber?.message}
                    />
                    <InputField
                        id="sport"
                        type="text"
                        label="Sport"
                        required
                        {...register("sport")}
                        error={errors.sport?.message}
                    />
                    <InputField
                        id="clubAndAddress"
                        type="text"
                        label="Vereniging en plaats"
                        required
                        {...register("clubAndAddress")}
                        error={errors.clubAndAddress?.message}
                    />
                    <Controller
                        control={control}
                        name="function"
                        render={({ field }) => (
                            <RadioGroup
                                onValueChange={field.onChange}
                                error={errors.function?.message}
                                label="Jouw rol binnen de vereniging"
                                options={
                                    [
                                        {
                                            value: "coach",
                                            label: "Coach"
                                        },
                                        {
                                            value: "trainer",
                                            label: "Trainer"
                                        },
                                        {
                                            value: "bestuur",
                                            label: "Bestuur"
                                        },
                                        {
                                            value: "sportbond",
                                            label: "Sportbond"
                                        },
                                        {
                                            value: "overig",
                                            label: "Overig"
                                        }
                                    ]
                                }
                            />
                        )}
                    />
                    <ButtonWrapper>
                        <Button isLoading={fetching} color="red" type="submit">Verstuur</Button>
                    </ButtonWrapper>
                </StyledForm>
            </FormContainer>
        </Container>
    </Wrapper>
}

export default ApplyForm
